import React from 'react';
import Glide from '@glidejs/glide';
import { setGlideSelector } from 'components/Blocks/utils';
import { MultiPhotoSlideWithText } from './MultiPhotoSlideWithText';

const defaultPeek = 0;
const peek = {
    /* eslint-disable no-nested-ternary */
    before: !isNaN(defaultPeek)
        ? defaultPeek
        : window.innerWidth < 600
        ? 86
        : window.innerWidth < 1000
        ? 240
        : window.innerWidth < 1200
        ? 90
        : 325,
    after: 0,
};
const defaultOptions = {
    type: 'carousel',
    focusAt: 'center',
    gap: 24,
    peek,
    throttle: 300,
} as Glide.Options;

type MultiPhotoSliderWithTextProps = {
    peek?: string;
    time?: string;
    block?: any;
    title?: string;
    perView?: string;
    pauseOnHover?: string;
    ctaText?: string;
    ctaLink?: string;
    ctaTextTwo?: string;
    ctaLinkTwo?: string;
};

function MultiPhotoSliderWithText(props: MultiPhotoSliderWithTextProps) {
    const blocks = props.block.innerBlocks.map((block: any) =>
        Object.fromEntries(block.attributes.map((item: any) => [item.name, item.value]))
    );
    const wrapperRef = React.useRef<HTMLDivElement>(null);
    const glideRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        let glides: Glide;
        let paused: boolean;
        let index: number;
        const defaultTime = 0;
        const pauseOnHover = false;

        function mountSlides() {
            if (wrapperRef.current !== null) {
                wrapperRef.current.style.opacity = '1';

                glides = new Glide(setGlideSelector(glideRef.current), {
                    ...defaultOptions,
                    startAt: index ?? 1,
                    autoplay: paused ? false : defaultTime || 4000,
                    hoverpause: true || pauseOnHover,
                    perView: 1,
                    cloningRatio: 1,
                });

                glides.mount();

                const selector = glides.selector as unknown as HTMLDivElement;

                glides.on('move', () => {
                    selector.classList.add('glide__moving');
                });
                glides.on('move.after', () => {
                    setTimeout(() => {
                        if (!selector.classList.contains('glide--dragging')) {
                            selector.classList.remove('glide__moving');
                        }
                        index = glides.index;
                    }, 50);
                });

                glides.on('swipe.end', () => {
                    setTimeout(() => {
                        glides.pause();
                        paused = true;
                    }, 1000);
                });
            } else {
                setTimeout(mountSlides, 200);
            }
        }

        mountSlides();

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    glides.enable();
                    glides.play();
                    // @ts-ignore
                    glides._c.Autoplay.start();
                } else {
                    glides.pause();
                    glides.disable();
                    // @ts-ignore
                    glides._c.Autoplay.stop();
                }
            });
        });

        observer.observe(glideRef.current!);

        return () => {
            glides.destroy();
            observer.disconnect();
        };
    }, []);

    return (
        <div className="wrapper black-bg white flex h-center v-center what" ref={wrapperRef}>
            <div className="content">
                {props.title ? (
                    <div className="black weight-black center-align beehive-h1">{props.title}</div>
                ) : null}

                <div className="wrapper">
                    <div className="glide glide--with-text" ref={glideRef}>
                        <div className="glide__track" data-glide-el="track">
                            <ul className="glide__slides">
                                {blocks.map((block: any, index: number) => (
                                    <MultiPhotoSlideWithText
                                        key={index}
                                        index={index}
                                        mediaURL={block.mediaURL}
                                        title={block.title}
                                        text={block.text}
                                        name={block.name}
                                        pronouns={block.pronouns}
                                    />
                                ))}
                            </ul>
                        </div>
                        <div className="glide__controls">
                            <div className="glide__arrows" data-glide-el="controls">
                                <button
                                    className="glide__arrow glide__arrow--left"
                                    data-glide-dir="<"
                                >
                                    <svg
                                        width="17"
                                        height="26"
                                        viewBox="0 0 17 26"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            opacity="0.4"
                                            d="M13 22L4 13L13 4"
                                            stroke="#FFC629"
                                            strokeWidth="8"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </button>
                            </div>

                            <div className="glide__bullets" data-glide-el="controls[nav]">
                                {blocks.map((_block: any, index: number) => (
                                    <button
                                        className="glide__bullet"
                                        data-glide-dir={`=${index}`}
                                        key={index}
                                    ></button>
                                ))}
                            </div>

                            <div className="glide__arrows" data-glide-el="controls">
                                <button
                                    className="glide__arrow glide__arrow--right"
                                    data-glide-dir=">"
                                >
                                    <svg
                                        width="17"
                                        height="26"
                                        viewBox="0 0 17 26"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path
                                            opacity="0.4"
                                            d="M4 4L13 13L4 22"
                                            stroke="#FFC629"
                                            strokeWidth="8"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="do-ctas flex h-center">
                    {props.ctaText && props.ctaLink ? (
                        <a className="weight-bold" href={props.ctaLink}>
                            {props.ctaText}
                        </a>
                    ) : null}
                    {props.ctaTextTwo && props.ctaLinkTwo ? (
                        <a className="weight-bold" href={props.ctaLinkTwo}>
                            {props.ctaTextTwo}
                        </a>
                    ) : null}
                </div>
            </div>
        </div>
    );
}

export { MultiPhotoSliderWithText };
